/** `tour.join` in the settings drawer (plan 2026-10-11 product joins §9, D4, D5): what saving the
 *  join the draft sets would do, and the answers it needs, before the save.
 *
 *  Whenever the draft sets or changes `tour.join`, `POST …/products/{pk}/join_preview/` (debounced;
 *  the save's own checks, writing nothing) answers with each of the product's IDs, who holds it now
 *  and what the save does with it, the product's bookings on those tours, and every refusal.
 *
 *  - **IDs another tour holds** (its own ID, or an additional ID of it) need a choice with no
 *    default: move them to the joined product's tour, or cancel the join (the draft's change is
 *    undone). A move spells out what happens to the old tour (`OFF/<id>`) and its bookings.
 *  - **The booking move** (D5): an unticked checkbox, shown only when the preview says the user may
 *    move bookings; ticking it asks the Edit-booking task's two questions, again with no default.
 *  - **A Tours setting on the product** cannot stay: a product that joins another builds no tour of
 *    its own, and the save refuses any grain but "No tour" (`join_grain`). The panel offers to reset
 *    Tours in the same save.
 *  - **Deferred**: while the joined product's tour does not exist, the save writes nothing; the IDs
 *    join that tour when its sync creates it, with the answers given now.
 *
 *  The save carries the answers as `join_consent`; the drawer keeps Save disabled until the
 *  preview of exactly this draft is in and every question is answered. The server checks again. */
import { Alert, AlertTitle, Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Fragment, useState } from 'react';

import * as catalogueApi from '@/api/catalogue';
import type {
  CatalogueProduct,
  JoinConsent,
  JoinPreview,
  JoinPreviewBody,
  SettingSpec,
} from '@/api/catalogue';
import { catalogueKeys } from '@/api/catalogue';
import type { BookingMoveChoice, ProductJoinProblem } from '@/api/sync';

import {
  answered,
  BookingMoveAnswers,
  consentTours,
  holdersOfMember,
  listLabels,
  OwnershipBlock,
  sumImpact,
  tourLabel,
  type HoldingTour,
  type OwnershipChoice,
} from '../JoinOwnership';
import { draftGet, draftHas, KEY, valueLabel, type SettingsDraft } from './catalogueModel';
import { useDebouncedValue } from './NameControl';

/** How long the draft must stand still before the preview is asked. */
export const JOIN_PREVIEW_DEBOUNCE_MS = 300;

/** Problems a prompt on screen already asks about: listing them too would say it twice. */
const OWNERSHIP_PROMPT = new Set(['owned_elsewhere']);
const ANSWERS_PROMPT = new Set(['booking_move_answers_missing', 'booking_move_unanswered']);
const GRAIN_PROBLEM = 'join_grain';
const PACKAGE_WHOLE_PROBLEM = 'join_package_whole';

export interface JoinDraft {
  /** The vendor id the draft joins the product to; null when the draft sets no join. */
  target: string | null;
  preview: JoinPreview | undefined;
  /** The preview in hand is for exactly this draft. */
  fresh: boolean;
  loading: boolean;
  error: Error | null;
  holders: HoldingTour[];
  /** The ownership answer, while it still covers every tour that holds the IDs. */
  choice: OwnershipChoice | undefined;
  /** The booking-move checkbox is offered (a move, the user may, future bookings exist). */
  canMoveBookings: boolean;
  moveBookings: boolean;
  answers: Partial<BookingMoveChoice>;
  /** The booking-move questions are on screen. */
  radiosShown: boolean;
  /** The product keeps a Tours setting the join refuses; the draft does not reset it. */
  grainBlocks: boolean;
  /** The draft resets Tours (or sets "No tour") in this save. */
  grainReset: boolean;
  /** Refusals no prompt on screen covers. */
  problems: ProductJoinProblem[];
  /** Why Save waits, or null when the join is ready to save (or there is none). */
  waitingFor: string | null;
  /** The save's `join_consent`, when ops consented to a move. */
  consent: JoinConsent | undefined;
  setChoice: (choice: OwnershipChoice | undefined) => void;
  setMoveBookings: (on: boolean) => void;
  setAnswers: (answers: Partial<BookingMoveChoice>) => void;
  /** Forget the answers (after a save). */
  reset: () => void;
  /** Ask the preview again (after a refused save). */
  refetch: () => void;
}

/** The join the draft sets, its preview and ops' answers. */
export function useJoinDraft(
  tenantId: number,
  product: CatalogueProduct | undefined,
  draft: SettingsDraft,
): JoinDraft {
  const [rawChoice, setRawChoice] = useState<OwnershipChoice | undefined>(undefined);
  const [moveBookingsRaw, setMoveBookings] = useState(false);
  const [answers, setAnswers] = useState<Partial<BookingMoveChoice>>({});
  const reset = () => {
    setRawChoice(undefined);
    setMoveBookings(false);
    setAnswers({});
  };
  // A new choice asks the booking move again (the checkbox is off by default).
  const setChoice = (choice: OwnershipChoice | undefined) => {
    setRawChoice(choice);
    setMoveBookings(false);
  };

  const raw = draftHas(draft, KEY.join) ? draftGet(draft, KEY.join) : undefined;
  const target = product && typeof raw === 'string' && raw.trim() ? raw.trim() : null;
  // Answers are given for one target: another one asks again.
  const [answeredFor, setAnsweredFor] = useState(target);
  if (answeredFor !== target) {
    setAnsweredFor(target);
    reset();
  }

  // The request carries the raw answers: a consent that no longer covers the holders is still
  // sent, and the server reports it.
  const consentOf = (moving: boolean): JoinConsent | undefined =>
    rawChoice?.kind === 'move'
      ? {
          move_from_tour_ids: rawChoice.tours,
          move_bookings: moving,
          booking_move:
            moving && answers.update_events && answers.old_tour_tags
              ? { update_events: answers.update_events, old_tour_tags: answers.old_tour_tags }
              : null,
        }
      : undefined;
  const body: JoinPreviewBody | null = target
    ? { target, ...(consentOf(moveBookingsRaw) ? { join_consent: consentOf(moveBookingsRaw) } : {}) }
    : null;
  const requestKey = JSON.stringify(body);
  const debouncedKey = useDebouncedValue(requestKey, JOIN_PREVIEW_DEBOUNCE_MS);
  const asked = JSON.parse(debouncedKey) as JoinPreviewBody | null;
  const productId = product?.id ?? 0;
  const query = useQuery({
    queryKey: asked ? catalogueKeys.joinPreview(tenantId, productId, asked) : ['catalogue', 'join-preview', 'none'],
    queryFn: ({ signal }) => catalogueApi.joinPreview(tenantId, productId, asked!, signal),
    enabled: asked !== null,
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 0,
  });
  const settled = debouncedKey === requestKey;
  const fresh = target !== null && settled && query.isSuccess && !query.isPlaceholderData;
  // A preview that could not answer does not hold the save: the server checks it again.
  const checking = target !== null && !fresh && !(settled && query.isError && !query.isFetching);
  // While another target's answer is still on screen, it says nothing about this one.
  const preview = target && query.data?.target.vendor_id === target ? query.data : undefined;

  const holders = preview ? holdersOfMember(preview.member) : [];
  const choice = holders.length > 0 && answered(rawChoice, holders) ? rawChoice : undefined;
  const unanswered = holders.length > 0 && !choice;
  const future = preview ? sumImpact(preview.member.impact).future : 0;
  const canMoveBookings = choice?.kind === 'move' && Boolean(preview?.can_move_bookings) && future > 0;
  const moveBookings = canMoveBookings && moveBookingsRaw;
  const radiosShown = moveBookings;
  const answersComplete = Boolean(answers.update_events && answers.old_tour_tags);

  // Tours: a joiner's grain must be none. A stored ops value blocks the save unless this draft
  // resets it; the server's join_grain says so too.
  const grainDraft = draftHas(draft, KEY.grain) ? draftGet(draft, KEY.grain) : undefined;
  const grainReset = draftHas(draft, KEY.grain) && (grainDraft === null || grainDraft === 'none');
  const grain = product?.resolution.product[KEY.grain];
  const grainSet =
    grainDraft !== undefined
      ? !grainReset
      : grain?.source === 'ops' && grain.value !== 'none';
  const serverGrain = Boolean(preview?.problems.some((p) => p.code === GRAIN_PROBLEM));
  const grainBlocks = target !== null && !grainReset && (grainSet || serverGrain);

  // The preview knows the stored settings plus the join, not the rest of this draft.
  const packageModeDraft = draftHas(draft, KEY.packageMode) ? draftGet(draft, KEY.packageMode) : undefined;
  const problems = (preview?.problems ?? []).filter(
    (p) =>
      !(OWNERSHIP_PROMPT.has(p.code) && unanswered) &&
      !(ANSWERS_PROMPT.has(p.code) && radiosShown) &&
      p.code !== GRAIN_PROBLEM &&
      !(p.code === PACKAGE_WHOLE_PROBLEM && packageModeDraft === 'legs'),
  );

  let waitingFor: string | null = null;
  if (target) {
    if (checking) {
      waitingFor = 'Checking the join…';
    } else if (unanswered) {
      waitingFor = 'Choose: move the IDs to the joined tour, or cancel the join.';
    } else if (radiosShown && !answersComplete) {
      waitingFor = 'Answer both booking-move questions.';
    } else if (grainBlocks) {
      waitingFor = 'Reset Tours in this save, or cancel the join.';
    } else if (fresh && problems.length > 0) {
      waitingFor = 'The join cannot be saved as it stands.';
    }
  }

  return {
    target,
    preview,
    fresh,
    loading: checking,
    error: target && query.isError ? (query.error as Error) : null,
    holders,
    choice,
    canMoveBookings,
    moveBookings,
    answers,
    radiosShown,
    grainBlocks,
    grainReset: target !== null && grainReset,
    problems,
    waitingFor,
    consent: choice?.kind === 'move' ? consentOf(moveBookings) : undefined,
    setChoice,
    setMoveBookings,
    setAnswers,
    reset,
    refetch: () => void query.refetch(),
  };
}

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

export function JoinPanel({
  join,
  product,
  integration,
  grainSpec,
  onResetGrain,
  onCancel,
}: {
  join: JoinDraft;
  product: CatalogueProduct;
  integration: string;
  grainSpec: SettingSpec | undefined;
  /** Reset Tours in this save. */
  onResetGrain: () => void;
  /** Cancel the join: undo the draft's change. */
  onCancel: () => void;
}) {
  if (!join.target) return null;
  const preview = join.preview;
  const name = product.title || product.name;
  const targetName = preview?.target.name || join.target;
  const destination = `${targetName}'s tour`;
  const grain = product.resolution.product[KEY.grain];
  const ids = preview?.member.ext_ids ?? [];

  return (
    <Stack spacing={1} sx={{ mt: 1 }} aria-label="Joining another product's tour" role="group">
      {join.loading ? <LinearProgress aria-label="Checking the join" /> : <Box sx={{ height: 4 }} />}
      {join.error && (
        <Alert severity="warning">
          The join could not be checked ({join.error.message}). The save checks it again.
        </Alert>
      )}
      {preview && (
        <Typography variant="body2">
          {name} builds no tour of its own.{' '}
          {ids.length > 0 && (
            <>
              Its {ids.length === 1 ? 'ID' : 'IDs'}{' '}
              {ids.map((e, i) => (
                <Fragment key={e.custom_id}>
                  {i > 0 && ', '}
                  <Box component="code" sx={{ fontFamily: MONO }}>
                    {e.custom_id}
                  </Box>
                  {e.action === 'already' && ' (already there)'}
                  {e.action === 'move' && e.owner && ` (moves from ${tourLabel({ id: e.owner.tour_id, name: e.owner.tour_name })})`}
                </Fragment>
              ))}{' '}
              {ids.length === 1 ? 'becomes an additional ID' : 'become additional IDs'} of {destination}
            </>
          )}
          {preview.target.tour_id !== null ? (
            <>
              {ids.length > 0 ? ': ' : `${destination} is `}
              {tourLabel({ id: preview.target.tour_id, name: preview.target.tour_name })} (#{preview.target.tour_id}
              {preview.target.key ? (
                <>
                  , keyed{' '}
                  <Box component="code" sx={{ fontFamily: MONO }}>
                    {preview.target.key}
                  </Box>
                </>
              ) : null}
              ).
            </>
          ) : (
            '.'
          )}
        </Typography>
      )}
      {preview?.deferred && (
        <Alert severity="info">
          {destination} does not exist yet, so the save writes nothing now: the IDs join that tour when
          its sync creates it{join.holders.length > 0 ? ', with the answers given here' : ''}.
        </Alert>
      )}
      {join.grainBlocks && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={onResetGrain}>
              Reset Tours in this save
            </Button>
          }
        >
          Tours is set to “{valueLabel(grainSpec, grain?.value)}” on {name}. A product that joins another
          builds no tour of its own: reset Tours with the join, or cancel the join.
        </Alert>
      )}
      {join.grainReset && (
        <Typography variant="caption" color="text.secondary">
          Tours resets to “No tour” in this save.
        </Typography>
      )}
      {join.problems.length > 0 && join.fresh && (
        <Alert severity="error">
          <AlertTitle>The save would be refused</AlertTitle>
          {join.problems.map((p, i) => (
            <div key={i}>{p.detail}</div>
          ))}
        </Alert>
      )}
      {join.holders.length > 0 && preview && (
        <OwnershipBlock
          name={name}
          integration={integration}
          holders={join.holders}
          choice={join.choice}
          impact={preview.member.impact}
          canMoveBookings={preview.can_move_bookings}
          moveBookings={join.moveBookings}
          destination={destination}
          leaveLabel={
            <>
              Cancel the join
              <Typography variant="caption" color="text.secondary" component="span">
                {' '}
                (undo this change: {listLabels(join.holders.map(tourLabel))} keep{join.holders.length === 1 ? 's' : ''} the IDs)
              </Typography>
            </>
          }
          onChoose={(kind) => {
            if (kind === 'leave') {
              join.reset();
              onCancel();
            } else {
              join.setChoice({ kind: 'move', tours: consentTours(join.holders) });
            }
          }}
          onMoveBookings={join.setMoveBookings}
        />
      )}
      {join.radiosShown && (
        <BookingMoveAnswers
          title="Moving bookings: answer both"
          answers={join.answers}
          onChange={join.setAnswers}
          oldTours={join.holders}
        />
      )}
    </Stack>
  );
}
